import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook para gerenciar permissões do usuário autenticado
 * @returns {Object} { permissions, hasPermission, loading, error }
 */
const usePermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar permissões do usuário
  const loadPermissions = useCallback(async () => {
    const controller = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const idusuario = parseInt(localStorage.getItem('sessionId'), 10);

      if (!idusuario) {
        setPermissions([]);
        setLoading(false);
        return controller;
      }

      // Buscar permissões efetivas do usuário (perfis + especiais)
      const response = await api.get(`/api/usuarios/${idusuario}/permissoes`, {
        signal: controller.signal
      });

      // Criar um mapa de permissões para acesso rápido
      const permissionsMap = response.data
        .filter(p => p.concedida === 1 || p.concedida === true)
        .map(p => ({
          recurso: p.recurso,
          acao: p.acao,
          modulo: p.modulo,
          key: `${p.recurso}:${p.acao}`
        }));

      setPermissions(permissionsMap);
    } catch (err) {
      if (err.name !== 'AbortError' && err.message !== 'Request aborted') {
        console.error('Erro ao carregar permissões:', err);
        setError(err);
        setPermissions([]);
      }
    } finally {
      setLoading(false);
    }

    return controller;
  }, []);

  useEffect(() => {
    const controller = loadPermissions();

    // Cleanup: abortar requisição se componente desmontar
    return () => {
      if (controller && controller.abort) {
        controller.abort();
      }
    };
  }, [loadPermissions]);

  /**
   * Verifica se o usuário tem uma permissão específica
   * @param {string} recurso - Nome do recurso (ex: 'cadastros', 'vendas')
   * @param {string} acao - Ação permitida (ex: 'read', 'create', 'update', 'delete')
   * @returns {boolean}
   */
  const hasPermission = useCallback((recurso, acao = 'read') => {
    if (!recurso) return true; // Se não especificou permissão, permite acesso

    const key = `${recurso}:${acao}`;
    return permissions.some(p => p.key === key);
  }, [permissions]);

  /**
   * Verifica se o usuário tem permissão para acessar um módulo inteiro
   * @param {string} modulo - Nome do módulo
   * @returns {boolean}
   */
  const hasModulePermission = useCallback((modulo) => {
    if (!modulo) return true;
    return permissions.some(p => p.modulo === modulo);
  }, [permissions]);

  /**
   * Verifica se o usuário tem pelo menos uma das permissões especificadas
   * @param {Array<{recurso: string, acao: string}>} requiredPermissions
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback((requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;

    return requiredPermissions.some(({ recurso, acao }) =>
      hasPermission(recurso, acao)
    );
  }, [hasPermission]);

  /**
   * Verifica se o usuário tem todas as permissões especificadas
   * @param {Array<{recurso: string, acao: string}>} requiredPermissions
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback((requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;

    return requiredPermissions.every(({ recurso, acao }) =>
      hasPermission(recurso, acao)
    );
  }, [hasPermission]);

  return {
    permissions,
    hasPermission,
    hasModulePermission,
    hasAnyPermission,
    hasAllPermissions,
    loading,
    error,
    reload: loadPermissions
  };
};

export default usePermissions;
